// src/services/http.js
import { storage } from "./storage";

const RAW_API_URL = process.env.REACT_APP_API_URL || "http://13.61.201.23:5000";
const API_BASE = RAW_API_URL.replace(/\/+$/, ""); // remove trailing slash(es)

const buildUrl = (path = "") => {
  // allow absolute urls if ever needed
  if (/^https?:\/\//i.test(path)) return path;

  const p = path.startsWith("/") ? path : `/${path}`;
  const baseHasApi = /\/api$/i.test(API_BASE);

  // If path already starts with /api, avoid double /api
  if (/^\/api(\/|$)/i.test(p)) {
    return baseHasApi
      ? `${API_BASE}${p.replace(/^\/api/i, "")}` // base already has /api -> remove it from path
      : `${API_BASE}${p}`;                      // base has no /api -> keep path as-is
  }

  // Path doesn't start with /api -> add /api if base doesn't have it
  return baseHasApi ? `${API_BASE}${p}` : `${API_BASE}/api${p}`;
};

const buildErrorMessage = (data, status) => {
  if (!data) return `HTTP ${status}: Request failed`;
  if (typeof data === "string") return `HTTP ${status}: ${data}`;

  if (data.message) return `HTTP ${status}: ${data.message}`;
  if (data.title) return `HTTP ${status}: ${data.title}`;

  if (data.errors && typeof data.errors === "object") {
    const all = Object.values(data.errors).flat();
    const msg = all.filter(Boolean).join("\n");
    return `HTTP ${status}: ${msg || "Validation error"}`;
  }

  try {
    return `HTTP ${status}: ${JSON.stringify(data)}`;
  } catch {
    return `HTTP ${status}: Request failed`;
  }
};

const isDev = process.env.NODE_ENV !== "production";

const isSensitiveKey = (k = "") => {
  const key = String(k).toLowerCase();
  return (
    key.includes("password") ||
    key.includes("pass") ||
    key.includes("token") ||
    key.includes("refresh") ||
    key.includes("secret")
  );
};

const maskObject = (obj) => {
  if (!obj || typeof obj !== "object") return obj;
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  for (const key of Object.keys(clone)) {
    if (isSensitiveKey(key)) clone[key] = "***";
  }
  return clone;
};

const summarizeFormData = (fd) => {
  const out = {};
  for (const [k, v] of fd.entries()) {
    const val =
      typeof v === "string"
        ? (isSensitiveKey(k) ? "***" : v)
        : `[${v?.constructor?.name || "Blob"}]`;

    if (k in out) out[k] = Array.isArray(out[k]) ? [...out[k], val] : [out[k], val];
    else out[k] = val;
  }
  return out;
};

export async function request(path, { method = "GET", body, auth = true, headers = {} } = {}) {
  const isFormData = body instanceof FormData;

  const finalHeaders = { ...headers };
  if (body && !isFormData) finalHeaders["Content-Type"] = "application/json";

  if (auth) {
    const token = storage.getToken();
    if (token) finalHeaders.Authorization = `Bearer ${token}`;
  }

  const url = buildUrl(path);

  if (isDev) {
    console.log("[HTTP]", method, url);
    if (body) {
      if (isFormData) console.log("[HTTP BODY]", summarizeFormData(body));
      else console.log("[HTTP BODY]", maskObject(body));
    }
  }

  const res = await fetch(url, {
    method,
    headers: finalHeaders,
    body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
  });

  if (res.status === 401) {
    storage.clear();
    throw new Error("UNAUTHORIZED");
  }

  const text = await res.text();
  const data = text
    ? (() => {
        try {
          return JSON.parse(text);
        } catch {
          return text;
        }
      })()
    : null;

  if (!res.ok) {
    throw new Error(buildErrorMessage(data, res.status));
  }

  return data;
}
